import { useCallback, useState } from "react";

import { Button, Text } from "@fluentui/react-components";
import {
  CheckmarkCircleFilled,
  CircleFilled,
  OpenFilled,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Flex } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";
import { useGetUserAppTargetSetting } from "hooks/useGetUserAppTargetSetting";
import { useIsDarkMode } from "hooks/useIsDarkMode";
import { APP_ID, APP_SETTING_ID } from "library/constants";
import { EAppSettings } from "library/types";
import { useAddAppSetting } from "state/queries/useAddAppSetting";
import { useUpdateAppSettings } from "state/queries/useUpdateAppSettings";
import { darkStyledTheme, styledTheme } from "theme/mainTheme";
import { generateTheme } from "../ThemeSelector/themeGenerator";
import { ThemeDialog } from "./ThemeDialog";
import { AppSpinner } from "components/AppSpinner/AppSpinner";

export function ThemeSettings() {
  const theme = useAppTheme();
  const { t } = useTranslation();
  const darkMode = useIsDarkMode();
  const [themeDialog, setThemeDialog] = useState(false);

  const mainTheme = darkMode ? darkStyledTheme : styledTheme;

  const mainColor = mainTheme.palette.purpleDark;

  const themeAppSetting = useGetUserAppTargetSetting({
    targetSettingName: EAppSettings.theme,
  });

  const { mutate: onAddAppSetting, isLoading: addSettingsInProgress } =
    useAddAppSetting();
  const { mutate: onUpdateAppSetting, isLoading: updateSettingsInProgress } =
    useUpdateAppSettings();

  const handleChangePrimaryColor = (color?: string) => {
    console.log(color, "color");
    const { lightTheme, darkTheme } = color
      ? generateTheme(color)
      : {
          lightTheme: styledTheme,
          darkTheme: darkStyledTheme,
        };
    console.log(lightTheme, "lightTheme");

    if (themeAppSetting) {
      onUpdateAppSetting({
        payload: [
          {
            ...themeAppSetting,
            userAppSettingValue: JSON.stringify({
              light: lightTheme,
              dark: darkTheme,
            }),
          },
        ] as any,
      });
    } else {
      onAddAppSetting({
        payload: [
          {
            applicationCodeId: APP_ID,
            userAppSettingValue: JSON.stringify({
              light: lightTheme,
              dark: darkTheme,
            }),
            appSettingCodeId: APP_SETTING_ID,
            userAppSettingName: EAppSettings.theme,
            isDefault: false,
          },
        ],
      });
    }
  };

  const onCloseThemeDialog = useCallback(() => {
    setThemeDialog(false);
  }, []);

  const themePalette = [
    "#005aa1",
    "#CA5010",
    "#986F0B",
    "#2e5c59",
    "#a4262c",
    "#00bcf2",
  ];

  const isLoading = addSettingsInProgress || updateSettingsInProgress;
  return (
    <>
      {isLoading && <AppSpinner />}
      <Flex direction="column" $align="flex-start" gap={"15px"}>
        <Text>{t("hbh.theme.label")}</Text>
        <Flex gap={"4px"}>
          <StyledButton
            appearance="transparent"
            onClick={() => {
              handleChangePrimaryColor();
            }}
            icon={
              mainColor === theme.palette.purpleDark ? (
                <CheckmarkCircleFilled
                  style={{
                    color: mainTheme.palette.purpleDark,
                  }}
                />
              ) : (
                <CircleFilled
                  style={{
                    color: mainTheme.palette.purpleDark,
                  }}
                />
              )
            }
          />
          {themePalette.map((color) => (
            <StyledButton
              appearance="transparent"
              onClick={() => {
                handleChangePrimaryColor(color);
              }}
              icon={
                color === theme.palette.purpleDark ? (
                  <CheckmarkCircleFilled
                    style={{
                      color: color,
                    }}
                  />
                ) : (
                  <CircleFilled
                    style={{
                      color: color,
                    }}
                  />
                )
              }
            />
          ))}
          <StyledButton
            onClick={() => {
              setThemeDialog(true);
            }}
            appearance="transparent"
            icon={<OpenFilled />}
          />
        </Flex>
      </Flex>
      <ThemeDialog open={themeDialog} onClose={onCloseThemeDialog} />
    </>
  );
}

const StyledButton = styled(Button)`
  padding: 0;
  margin: 0;
  min-width: fit-content;
`;
